import { SupportedLanguage } from 'oas';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { KEY_DEFAULT_LANGUAGE_WEB, useGetSetting } from 'api/settings';

export interface Translations {
    [key: string]: string;
}

// Translations are bundled with the app (one flat `module.key` map per language) rather than
// fetched from the backend. Each language is a separate dynamic import so the bundler emits one
// chunk per locale and only the active language (plus the English fallback) is downloaded.
const localeLoaders: Record<SupportedLanguage, () => Promise<{ default: Translations }>> = {
    en: () => import('../../i18n/locales/en.json'),
    es: () => import('../../i18n/locales/es.json'),
    fr: () => import('../../i18n/locales/fr.json'),
    ja: () => import('../../i18n/locales/ja.json'),
    ptbr: () => import('../../i18n/locales/ptbr.json'),
    zh: () => import('../../i18n/locales/zh.json'),
};

const isSupported = (value: unknown): value is SupportedLanguage => typeof value === 'string' && value in localeLoaders;

export const useGetTranslations = (lng?: SupportedLanguage): UseQueryResult<Translations> => {
    // When no language is requested, fall back to the user's saved preference, then English.
    const languageSetting = useGetSetting(KEY_DEFAULT_LANGUAGE_WEB);
    const requested = lng ?? languageSetting?.value;
    const language: SupportedLanguage = isSupported(requested) ? requested : 'en';

    return useQuery({
        // Language is part of the key so switching it loads the matching chunk; a manual
        // invalidation of ['translations'] still matches via prefix.
        queryKey: ['translations', language],
        queryFn: async () => (await localeLoaders[language]()).default,
        // Bundled data never goes stale and is available without authentication.
        staleTime: Infinity,
    });
};
